<script setup lang="ts">
	import type { Ref } from "vue";
	import type { IValue } from "~/interfaces";
	import { searchCity } from "~/utils/searchAutocomplete";

	import { useFormContainer } from "~/composables/useFormContainer";
	import FormCard from "~/components/ui/FormCard.vue";
	import PrecompiledBox from "~/components/ui/PrecompiledBox.vue";
	import PrecompiledField from "~/components/inputs/PrecompiledField.vue";
	import InputFloatingLabel from "~/components/inputs/InputFloatingLabel.vue";
	import SelectedAmount from "~/themes/cliente-unicredit/components/ui/SelectedAmount.vue";
	import RadioButton from "~/components/inputs/RadioButton.vue";
	import AutoComplete from "~/components/inputs/AutoComplete.vue";
	import Select from "~/components/inputs/Select.vue";

	import Validator from "~/utils/validator";

	const locale = await getLocale("forms", "delivery_data");
	import { useVuelidate } from "@vuelidate/core";
	import precompiledDataFormRules from "~/themes/cliente-unicredit/rules/precompiledDataFormRules";
	import jsonCities from "~/data/cities.json";

	defineComponent({
		name: "PrecompiledData",
	});

	defineProps({
		progress: { type: Number, required: true },
		title: { type: String, required: true },
		stickyButton: {type: Boolean, default: true}
	});

	const { FormContainer } = useFormContainer();

	const emit = defineEmits(["init", "submit"]);

	const editResidence = useState<boolean>("editResidence", () => false);
	const editIban = useState<boolean>("editIban", () => false);
	const address = useState<string | null>("address", () => null);
	const addressCity = useState<string | undefined>("addressCity", () => undefined);
	const addressCap = useState<string | null>("addressCap", () => null);
	const address2 = useState<string | null>("address2", () => null);
	const addressCity2 = useState<string | undefined>("addressCity2", () => undefined);
	const addressCap2 = useState<string | null>("addressCap2", () => null);
	const sameAddresses = useState<boolean>("sameAddresses", () => true);
	const iban = useState<string | null>("iban", () => null);
	const maskedIban = useState<string | null>("maskedIban", () => null);

	const credit = useState<number>("credit", () => 0);

	const postalCodes: Ref<IValue[]> = ref([]);
	const postalCodes2: Ref<IValue[]> = ref([]);

	onMounted(async () => {
		emit("init");
	});

	watch(addressCity, async (newValue) => {
		addressCap.value = null;
		postalCodes.value = [];

		const city = jsonCities.find(
			(city) => city.Citta == newValue?.slice(0, -5)
		);

		if (city) {
			if (city.Multicap) {
				postalCodes.value.push({
					value: null,
					label: locale.postal_code,
					disabled: true,
				});
				city.Multicap.split(",").map((cap: string) => {
					postalCodes.value.push({
						value: cap.padStart(5, "0"),
						label: cap.padStart(5, "0"),
					});
				});
			} else {
				addressCap.value = city.Cap.padStart(5, "0");
			}
		}
	});

	watch(addressCity2, async (newValue) => {
		addressCap2.value = null;
		postalCodes2.value = [];

		const city = jsonCities.find(
			(city) => city.Citta == newValue?.slice(0, -5)
		);

		if (city) {
			if (city.Multicap) {
				postalCodes2.value.push({
					value: null,
					label: locale.postal_code,
					disabled: true,
				});
				city.Multicap.split(",").map((cap: string) => {
					postalCodes2.value.push({
						value: cap.padStart(5, "0"),
						label: cap.padStart(5, "0"),
					});
				});
			} else {
				addressCap2.value = city.Cap.padStart(5, "0");
			}
		}
	});

	const v$ = useVuelidate(precompiledDataFormRules, {
		address,
		addressCity,
		addressCap,
		address2,
		addressCity2,
		addressCap2,
		sameAddresses,
		editIban,
		iban
	});

	const validate = () => {
		v$.value.$touch();
		v$.value.$validate();
		return !v$.value.$error;
	};
	const submit = () => {
		if (validate()) {
			emit("submit");
		}
	};
</script>

<template>
	<FormContainer class="precompiled-data" :progress="progress">

		<FormCard :title="title" @submit="submit" class="form-card" :has-sticky-button="stickyButton" :submit-text="'CONFERMA'">
			<template #before-header>
				<SelectedAmount :value="credit" :custom-class="'text-2xl'" />
			</template>

			<div class="px-4 w-full">
				<PrecompiledBox
					v-show="!editResidence"
					:value="editResidence"
					@update:value="editResidence = $event"
				>
					<PrecompiledField
						label="Indirizzo di residenza"
						v-model="address"
						class="sm:w-1/2"
					/>
					<PrecompiledField
						label="Comune di residenza"
						v-model="addressCity"
						class="sm:w-2/6"
					/>
					<PrecompiledField
						label="CAP"
						v-model="addressCap"
						class="sm:w-1/6"
					/>

					<div class="sm:mt-2">
						<p class="text-gray-3 text-sm font-semibold leading-none">
							Il domicilio coincide con la residenza?
						</p>
						<div class="uppercase flex mt-2 gap-1">
							<div
								class="px-2 py-1 text-sm w-fit font-semibold leading-none rounded-sm"
								:class="{ 'bg-gray-2': sameAddresses }"
								:aria-hidden="!sameAddresses"
							>
								sì
							</div>
							<div
								class="px-2 py-1 text-sm w-fit font-semibold leading-none rounded-sm"
								:class="{ 'text-gray-3': !sameAddresses }"
								:aria-hidden="sameAddresses"
							>
								no
							</div>
						</div>
					</div>
				</PrecompiledBox>

				<div
					v-show="editResidence"
					class="w-full border-b border-gray-2 px-1 pb-6 mb-6 pt-5"
				>
					<div class="lg:px-8 flex flex-wrap">
						<div class="w-full sm:w-1/2 sm:pr-4 mb-2">
							<InputFloatingLabel
								:mask="addressMask"
								:name="`address`"
								v-model="address"
								:label="`Indirizzo di residenza`"
								type="text"
								test-id="address"
							/>
						</div>
						<div class="w-full sm:w-2/6 mb-2 sm:pr-4">
							<AutoComplete
								:name="`addressCity`"
								v-model="addressCity"
								:clear-incomplete="true"
								:searchFunction="searchCity"
								:label="locale.residence_city"
								:uppercase="true"
								:error="v$.addressCity.$error"
								:error-message="Validator.message(v$.addressCity)"
								test-id="addressCity"
								type="text"
							/>
						</div>
						<div class="w-1/2 sm:w-1/6 mb-2">
							<Select
								v-if="postalCodes.length"
								v-model="addressCap"
								:values="postalCodes"
								label="CAP"
								floating
								disable-zero-value
								:error="v$.addressCap.$error"
								:error-message="Validator.message(v$.addressCap)"
								test-id="addressCap"
							/>
							<InputFloatingLabel
								v-else
								v-model="addressCap"
								:mask="addressStreetNumberMask"
								label="CAP"
								type="text"
								:error="v$.addressCap.$error"
								:error-message="Validator.message(v$.addressCap)"
								disabled
								test-id="addressCap"
							/>
						</div>
					</div>

					<div class="w-full mb-2 lg:px-8 mt-4 sm:mt-0">
						<label class="text-xs">{{
							"Il domicilio coincide con la residenza"
						}}</label>
						<div class="flex mt-1">
							<div class="relative mr-4">
								<RadioButton
									v-model="sameAddresses"
									:values="yesNoOptions"
									:error="v$.sameAddresses.$error"
									:error-message="Validator.message(v$.sameAddresses)"
									data-testid="sameAddresses"
								/>
							</div>
						</div>
					</div>

					<div v-if="!sameAddresses">
						<div class="lg:px-8 pt-5 flex flex-wrap">
							<div class="w-full sm:w-3/6 sm:pr-4 mb-2">
								<InputFloatingLabel
									:name="`address2`"
									v-model="address2"
									:mask="addressMask"
									type="text"
									:label="`Indirizzo di domicilio`"
									:error="v$.address2.$error"
									:error-message="Validator.message(v$.address2)"
								/>
							</div>
							<div class="w-full sm:w-2/6 sm:pr-4 mb-2">
								<AutoComplete
									:name="`addressCity2`"
									v-model="addressCity2"
									:clear-incomplete="true"
									:searchFunction="searchCity"
									:label="locale.domicile_city"
									uppercase
									:error="v$.addressCity2.$error"
									:error-message="Validator.message(v$.addressCity2)"
									test-id="addressCity2"
									type="text"
								/>
							</div>
							<div class="w-1/2 sm:w-1/6 mb-2">
								<Select
									v-if="postalCodes2.length"
									v-model="addressCap2"
									:values="postalCodes2"
									label="CAP"
									floating
									disable-zero-value
									:error="v$.addressCap2.$error"
									:error-message="Validator.message(v$.addressCap2)"
									test-id="addressCap2"
								/>
								<InputFloatingLabel
									v-else
									:name="`addressCap2`"
									v-model="addressCap2"
									:label="`CAP`"
									type="text"
									:error="v$.addressCap2.$error"
									:error-message="Validator.message(v$.addressCap2)"
									disabled
									test-id="addressCap2"
								/>
							</div>
						</div>
					</div>
				</div>

				<PrecompiledBox
					v-if="!editIban && maskedIban"
					:value="editIban"
					:class="{ 'mt-2': editResidence }"
					@update:value="editIban = $event"
				>
					<PrecompiledField label="IBAN del tuo conto corrente UniCredit su cui verseremo l'importo richiesto" v-model="maskedIban" />
				</PrecompiledBox>

				<div v-else class="w-full px-1 pb-6 pt-3">
					<div class="w-full lg:w-1/2 lg:px-8">
						<InputFloatingLabel
							:name="`iban`"
							type="text"
							v-model="iban"
							:label="`IBAN del tuo conto corrente`"
							:error="v$.iban.$error"
							:error-message="Validator.message(v$.iban)"
							test-id="iban"
						/>
					</div>
				</div>
			</div>
		</FormCard>

		<template #bottom-out>
			<div class="mx-auto text-gray-4 px-4 pb-6">
				<p class="opacity-60">
					<strong class="font-semibold">Perché conosciamo i tuoi dati?</strong>
					La risposta è <strong class="font-semibold">semplice</strong> e
					<strong class="font-semibold">trasparente</strong>: hai già una delle
					soluzioni di credito con Cofidis
				</p>
			</div>
		</template>
	</FormContainer>
</template>